/**
 * Administration → Auto provision — per-SITE default provisioning
 * template + watcher toggle.
 *
 * IMPORTANT SCOPE NOTE: everything on this panel is stored per Gigafy
 * location (site), NOT per chassis. Saving here changes the behavior
 * of EVERY compatible chassis at the site — the banner + per-chassis
 * schedule table make that explicit. The per-chassis
 * ``auto_provision`` schedule rows shown in the table are derived
 * executors managed by the backend sync (the Schedules UI refuses to
 * edit them directly).
 *
 * Watcher semantics (backend): every 10 min (2 min while boosted) the
 * worker scans the chassis for pending ONUs and provisions each one
 * with this template — returning serials go back to their prior
 * location, brand-new serials get the next free id on the port the
 * OLT saw them on. A serial that fails ``max_auto_attempts`` times is
 * parked (skipped by the watcher, still manually provisionable on the
 * Pending tab).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Play, Save, Zap, ZapOff } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { api, apiErrorMessage } from "../../../../api/client";
import { formatLocal, LocalTime } from "../../../../lib/datetime";
import { Badge } from "../../../components/Badge";
import {
  ServiceSpecsEditor,
  type ServiceSpecRow,
} from "../../../components/ServiceSpecsEditor";
import { useToast } from "../../../components/Toast";

type ProfileRow = {
  id: number;
  category: string;
  name: string;
  usage_count: number;
};
type ProfileListResponse = {
  profiles: ProfileRow[];
  latest_backup_id: number | null;
  parsed_profiles_at: string | null;
};

type ServiceSpecWire = {
  vnet_profile: string;
  upstream?: string | null;
  downstream?: string | null;
  cosq_profile?: string | null;
};

type SiteChassisSchedule = {
  chassis_id: number;
  device_name: string | null;
  chassis_enabled: boolean;
  schedule_id: number | null;
  schedule_enabled: boolean;
  interval_seconds: number | null;
  last_run_at: string | null;
  next_run_at: string | null;
};

type SiteProvisionDefaults = {
  gigafy_location_id: string;
  adapter_key: string;
  template: ServiceSpecWire[];
  auto_provision_enabled: boolean;
  boost_until: string | null;
  boost_active: boolean;
  max_auto_attempts: number;
  updated_by: string | null;
  updated_at: string | null;
  chassis: SiteChassisSchedule[];
};

function wireToRows(template: ServiceSpecWire[]): ServiceSpecRow[] {
  return template.map((s) => ({
    vnet_profile: s.vnet_profile ?? "",
    upstream: s.upstream ?? "",
    downstream: s.downstream ?? "",
    cosq_profile: s.cosq_profile ?? "",
  }));
}

function rowsToWire(rows: ServiceSpecRow[]): ServiceSpecWire[] {
  return rows
    .filter((s) => s.vnet_profile.trim() !== "")
    .map((s) => ({
      vnet_profile: s.vnet_profile.trim(),
      ...(s.upstream.trim() ? { upstream: s.upstream.trim() } : {}),
      ...(s.downstream.trim() ? { downstream: s.downstream.trim() } : {}),
      ...(s.cosq_profile.trim()
        ? { cosq_profile: s.cosq_profile.trim() }
        : {}),
    }));
}

export function AutoProvisionPanel({
  chassisId,
  adapterKey,
  locationId,
  locationName,
}: {
  chassisId: string;
  adapterKey: string;
  locationId: string | null;
  locationName: string | null;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const defaultsKey = [
    "v2",
    "site-provision-defaults",
    locationId,
    adapterKey,
  ];
  const defaultsQ = useQuery<SiteProvisionDefaults>({
    queryKey: defaultsKey,
    enabled: !!locationId,
    queryFn: async () => {
      const res = await api.get<SiteProvisionDefaults>(
        `/locations/${locationId}/provision-defaults`,
        { params: { adapter_key: adapterKey } },
      );
      return res.data;
    },
  });

  // This chassis's parsed profiles feed the editor dropdowns (any
  // chassis at the site works — they share OLT config conventions).
  const profilesQ = useQuery<ProfileListResponse>({
    queryKey: ["v2", "chassis", chassisId, "profiles"],
    queryFn: async () => {
      const res = await api.get<ProfileListResponse>(
        `/chassis/${chassisId}/profiles`,
      );
      return res.data;
    },
  });
  const known = useMemo(() => {
    const list = profilesQ.data?.profiles ?? [];
    const byCat = (cat: string) =>
      Array.from(
        new Set(list.filter((p) => p.category === cat).map((p) => p.name)),
      ).sort();
    return {
      vnet: byCat("vnet"),
      bandwidth: byCat("bandwidth"),
      shaper: byCat("shaper"),
      cosq: byCat("cosq"),
    };
  }, [profilesQ.data]);
  const defaultCosqProfile = useMemo<string | null>(() => {
    const cosq = (profilesQ.data?.profiles ?? []).filter(
      (p) => p.category === "cosq",
    );
    if (cosq.length === 0) return null;
    return [...cosq].sort(
      (a, b) => b.usage_count - a.usage_count || a.name.localeCompare(b.name),
    )[0].name;
  }, [profilesQ.data]);

  // Editable state, re-seeded whenever the server row changes
  // (OverridesPanel pattern).
  const [rows, setRows] = useState<ServiceSpecRow[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [maxAttempts, setMaxAttempts] = useState(3);
  useEffect(() => {
    if (!defaultsQ.data) return;
    setRows(wireToRows(defaultsQ.data.template));
    setEnabled(defaultsQ.data.auto_provision_enabled);
    setMaxAttempts(defaultsQ.data.max_auto_attempts);
  }, [defaultsQ.data]);

  const dirty = useMemo(() => {
    if (!defaultsQ.data) return false;
    return (
      enabled !== defaultsQ.data.auto_provision_enabled ||
      maxAttempts !== defaultsQ.data.max_auto_attempts ||
      JSON.stringify(rowsToWire(rows)) !==
        JSON.stringify(rowsToWire(wireToRows(defaultsQ.data.template)))
    );
  }, [defaultsQ.data, rows, enabled, maxAttempts]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.put<SiteProvisionDefaults>(
        `/locations/${locationId}/provision-defaults`,
        {
          template: rowsToWire(rows),
          auto_provision_enabled: enabled,
          max_auto_attempts: maxAttempts,
        },
        { params: { adapter_key: adapterKey } },
      );
      return res.data;
    },
    onSuccess: (d) => {
      queryClient.setQueryData(defaultsKey, d);
      queryClient.invalidateQueries({
        queryKey: ["v2", "site-provision-defaults"],
      });
      toast.push(
        d.auto_provision_enabled
          ? "Saved — auto-provision is ON for this site"
          : "Saved — auto-provision is off",
        "success",
      );
    },
    onError: (err) =>
      toast.push(apiErrorMessage(err, "Save failed"), "error"),
  });

  const boost = useMutation({
    mutationFn: async (arm: boolean) => {
      const res = arm
        ? await api.post<SiteProvisionDefaults>(
            `/locations/${locationId}/provision-defaults/boost`,
            {},
            { params: { adapter_key: adapterKey } },
          )
        : await api.delete<SiteProvisionDefaults>(
            `/locations/${locationId}/provision-defaults/boost`,
            { params: { adapter_key: adapterKey } },
          );
      return res.data;
    },
    onSuccess: (d) => {
      queryClient.setQueryData(defaultsKey, d);
      toast.push(
        d.boost_active
          ? `Boost armed — 2-minute checks until ${formatLocal(d.boost_until)}`
          : "Boost cancelled — back to 10-minute checks",
        "success",
      );
    },
    onError: (err) => toast.push(apiErrorMessage(err, "Boost failed"), "error"),
  });

  // Force a cycle right now — bypasses the 10-minute interval AND the
  // staff-active hold (pressing the button IS the staff activity), and
  // supersedes any queued auto-provision work for this chassis.
  const runNow = useMutation({
    mutationFn: async () => {
      const res = await api.post<{ job_id: string; superseded: string[] }>(
        `/chassis/${chassisId}/auto-provision/run-now`,
        {},
      );
      return res.data;
    },
    onSuccess: (d) => {
      toast.push(
        d.superseded.length
          ? `Scan started (superseded ${d.superseded.length} queued run${d.superseded.length === 1 ? "" : "s"})`
          : "Scan started — watch the pending list and Jobs for results",
        "success",
      );
      window.dispatchEvent(
        new CustomEvent("inline-job-tracker.add", {
          // InlineJobTracker reads ``detail.jobId`` — ``id`` was silently dropped.
          detail: { jobId: d.job_id, kind: "auto_provision", label: "Auto-provision (forced)" },
        }),
      );
      queryClient.invalidateQueries({
        queryKey: ["v2", "chassis", chassisId, "pending"],
      });
    },
    onError: (err) =>
      toast.push(apiErrorMessage(err, "Run-now failed"), "error"),
  });

  if (!locationId) {
    return (
      <section className="rounded-lg border border-slate-800 bg-slate-900 p-4 text-xs text-slate-400">
        This chassis has no Gigafy site linked — auto provision is
        configured per site, so link a location first.
      </section>
    );
  }

  const data = defaultsQ.data;
  const validServices = rowsToWire(rows).length;

  return (
    <section className="space-y-3 rounded-lg border border-slate-800 bg-slate-900 p-4">
      {/* Site-scope banner */}
      <div className="rounded border border-sky-900/70 bg-sky-950/40 p-2 text-[11px] text-sky-200">
        These settings apply to <strong>ALL {adapterKey} chassis</strong> at{" "}
        <strong>{locationName ?? locationId}</strong>
        {data ? ` (${data.chassis.length} chassis)` : ""} — every one of them
        watches for pending ONUs and provisions them with this template while
        enabled.
      </div>

      {defaultsQ.isLoading ? (
        <p className="text-xs text-slate-500">Loading…</p>
      ) : defaultsQ.isError ? (
        <p className="text-xs text-red-400">Failed to load site defaults.</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-xs text-slate-200">
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="h-3.5 w-3.5 accent-emerald-600"
              />
              Auto provision enabled
              <Badge tone={enabled ? "success" : "muted"}>
                {enabled ? "ON" : "off"}
              </Badge>
            </label>
            <label className="inline-flex items-center gap-2 text-xs text-slate-400">
              Park after
              <input
                type="number"
                min={1}
                max={10}
                value={maxAttempts}
                onChange={(e) =>
                  setMaxAttempts(
                    Math.max(1, Math.min(10, Number(e.target.value) || 1)),
                  )
                }
                className="w-14 rounded border border-slate-700 bg-slate-950 px-2 py-1 font-mono text-xs text-slate-100"
              />
              failed auto attempts
            </label>
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={
                save.isPending || !dirty || (enabled && validServices === 0)
              }
              className="inline-flex items-center gap-1 rounded border border-emerald-800 bg-emerald-950 px-3 py-1.5 text-xs text-emerald-200 hover:bg-emerald-900 disabled:opacity-40"
            >
              <Save className="h-3.5 w-3.5" />
              {save.isPending ? "Saving…" : "Save site defaults"}
            </button>
            {enabled && validServices === 0 && (
              <span className="text-[11px] text-amber-300">
                add at least one service before enabling
              </span>
            )}
          </div>

          {/* Boost — 24 h of 2-minute cycles, then back to 10 min. */}
          <div className="flex flex-wrap items-center gap-3 rounded border border-slate-800 bg-slate-950/40 p-2">
            <button
              type="button"
              onClick={() => runNow.mutate()}
              disabled={runNow.isPending || !data?.auto_provision_enabled}
              title={
                data?.auto_provision_enabled
                  ? "Run a scan + provision cycle on THIS chassis immediately — skips the 10-minute wait and the staff-activity hold."
                  : "Enable + save auto-provision first."
              }
              className="inline-flex items-center gap-1 rounded border border-emerald-800 bg-emerald-950 px-2 py-1 text-[11px] text-emerald-200 hover:bg-emerald-900 disabled:opacity-40"
            >
              <Play className="h-3 w-3" />
              {runNow.isPending ? "Starting…" : "Scan & provision now"}
            </button>
            {data?.boost_active ? (
              <>
                <Badge tone="warning">
                  boosted — 2-min checks until {formatLocal(data.boost_until)}
                </Badge>
                <button
                  type="button"
                  onClick={() => boost.mutate(false)}
                  disabled={boost.isPending}
                  className="inline-flex items-center gap-1 rounded border border-slate-700 px-2 py-1 text-[11px] text-slate-200 hover:bg-slate-800 disabled:opacity-40"
                >
                  <ZapOff className="h-3 w-3" />
                  Cancel boost
                </button>
              </>
            ) : (
              <button
                type="button"
                onClick={() => boost.mutate(true)}
                disabled={
                  boost.isPending || !data?.auto_provision_enabled
                }
                title={
                  data?.auto_provision_enabled
                    ? "Watch this site closely: 2-minute checks for the next 24 hours, then back to 10 minutes (auto-provision stays on)."
                    : "Enable + save auto-provision first."
                }
                className="inline-flex items-center gap-1 rounded border border-amber-800 bg-amber-950 px-2 py-1 text-[11px] text-amber-200 hover:bg-amber-900 disabled:opacity-40"
              >
                <Zap className="h-3 w-3" />
                Boost to 2-min checks for 24 h
              </button>
            )}
            <span className="text-[10px] text-slate-500">
              Normal cadence: every 10 minutes while enabled.
            </span>
          </div>

          <ServiceSpecsEditor
            services={rows}
            onChange={setRows}
            known={known}
            defaultCosqProfile={defaultCosqProfile}
            minRows={0}
            noParsedProfiles={profilesQ.data?.latest_backup_id == null}
          />
          {profilesQ.data?.latest_backup_id == null && (
            <p className="text-[10px] text-amber-300">
              No backup parsed yet — values can't be validated, and an empty
              COS falls back to the chassis default at provision time.
            </p>
          )}

          {/* Per-chassis watcher status (derived schedule rows). */}
          {data && data.chassis.length > 0 && (
            <div className="space-y-1">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-300">
                Watchers at this site
              </h4>
              <table className="w-full text-left text-[11px]">
                <thead className="text-slate-500">
                  <tr>
                    <th className="py-1 pr-2 font-normal">Chassis</th>
                    <th className="py-1 pr-2 font-normal">Watcher</th>
                    <th className="py-1 pr-2 font-normal">Interval</th>
                    <th className="py-1 pr-2 font-normal">Last run</th>
                    <th className="py-1 font-normal">Next run</th>
                  </tr>
                </thead>
                <tbody className="text-slate-200">
                  {data.chassis.map((c) => (
                    <tr key={c.chassis_id} className="border-t border-slate-800">
                      <td className="py-1 pr-2">
                        {c.device_name ?? `#${c.chassis_id}`}
                        {!c.chassis_enabled && (
                          <Badge tone="muted">chassis disabled</Badge>
                        )}
                      </td>
                      <td className="py-1 pr-2">
                        <Badge tone={c.schedule_enabled ? "success" : "muted"}>
                          {c.schedule_enabled ? "armed" : "off"}
                        </Badge>
                      </td>
                      <td className="py-1 pr-2 font-mono">
                        {c.interval_seconds != null
                          ? `${Math.round(c.interval_seconds / 60)} min`
                          : "—"}
                      </td>
                      <td className="py-1 pr-2 font-mono text-[10px] text-slate-400">
                        <LocalTime iso={c.last_run_at} />
                      </td>
                      <td className="py-1 font-mono text-[10px] text-slate-400">
                        <LocalTime iso={c.next_run_at} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data?.updated_at && (
            <p className="text-[10px] text-slate-500">
              Last saved by {data.updated_by ?? "—"} ·{" "}
              <LocalTime iso={data.updated_at} />
            </p>
          )}
        </>
      )}
    </section>
  );
}
